import jwtDecode from 'jwt-decode';
import { PLUSMAX_USERLEVEL } from './planTier';

const INVITATION_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;
const REDEEM_TIMEOUT_MS = 12000;
const pendingRedemptions = new Map();

export const isInvitationTrial = (decoded) => decoded?.trialKind === 'invitation';

// This module is the first import in index.js. Keep the invitation secret out
// of the URL before App, analytics SDKs, or replay can observe the page.
export const captureInvitationFragment = (browserWindow) => {
  if (!browserWindow) return null;
  const params = new URLSearchParams(String(browserWindow.location.hash || '').replace(/^#/, ''));
  if (!params.has('trialInvite')) return null;
  const token = params.get('trialInvite') || '';
  const valid = params.getAll('trialInvite').length === 1 && INVITATION_TOKEN_PATTERN.test(token);
  try {
    browserWindow.history.replaceState(
      browserWindow.history.state,
      '',
      `${browserWindow.location.pathname}${browserWindow.location.search}`
    );
  } catch {
    // Fail closed: do not continue module/analytics initialization with a secret URL.
    throw new Error('Unable to clear the invitation link from the browser URL.');
  }
  return { token: valid ? token : '', valid };
};

let capturedInvitation = captureInvitationFragment(typeof window === 'undefined' ? null : window);
const invitationSubscribers = new Set();
export const getCapturedInvitation = () => capturedInvitation;
export const subscribeToInvitations = (listener) => {
  invitationSubscribers.add(listener);
  return () => invitationSubscribers.delete(listener);
};

if (typeof window !== 'undefined') {
  // A link opened while CASADATA is already on / can be hash-only navigation.
  // Register before SDK listeners so they observe the cleaned URL as well.
  window.addEventListener('hashchange', () => {
    const invitation = captureInvitationFragment(window);
    if (!invitation) return;
    capturedInvitation = invitation;
    invitationSubscribers.forEach((listener) => listener(invitation));
  });
}

const invitationError = (code) => Object.assign(new Error('Invitation access could not be restored.'), { code });

export const validateInvitationResponse = (data, now = Date.now()) => {
  try {
    if (typeof data?.token !== 'string' || typeof data?.expiresAt !== 'string') throw invitationError('unavailable');
    const decoded = jwtDecode(data.token);
    const expiresAtMs = Date.parse(data.expiresAt);
    const tokenExpiryMs = Number(decoded.exp) * 1000;
    if (!isInvitationTrial(decoded) || decoded.delta !== PLUSMAX_USERLEVEL ||
        decoded.initial !== 'TEMPORARY' || !decoded.invitationId ||
        !Number.isFinite(tokenExpiryMs) || !Number.isFinite(expiresAtMs) ||
        Math.abs(tokenExpiryMs - expiresAtMs) >= 1000) {
      throw invitationError('unavailable');
    }
    if (tokenExpiryMs <= now) throw invitationError('expired');
    return { token: data.token, decoded, expiresAt: data.expiresAt };
  } catch (error) {
    throw invitationError(error?.code === 'expired' ? 'expired' : 'unavailable');
  }
};

export const redeemInvitationTrial = (invitation, serverUrl, fetchRequest = fetch) => {
  if (!invitation?.valid || !INVITATION_TOKEN_PATTERN.test(invitation.token || '')) {
    return Promise.reject(invitationError('invalid'));
  }
  // React StrictMode mounts twice. Share the request without consuming the
  // captured secret. Only pending requests are shared, so reopening the link
  // after completion checks the server again without renewing its deadline.
  const key = invitation.token;
  if (pendingRedemptions.has(key)) return pendingRedemptions.get(key);
  const request = (async () => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REDEEM_TIMEOUT_MS);
    try {
      const response = await fetchRequest(`${serverUrl}/api/invitationTrial/redeem`, {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({ token: invitation.token })
      });
      if (!response.ok) {
        throw invitationError(response.status === 410 ? 'expired' :
          [400, 401, 403, 404].includes(response.status) ? 'invalid' : 'unavailable');
      }
      return validateInvitationResponse(await response.json());
    } catch (error) {
      // Never propagate request/server payloads, which could contain secrets.
      throw invitationError(['invalid', 'expired'].includes(error?.code) ? error.code : 'unavailable');
    } finally {
      clearTimeout(timeout);
    }
  })();
  pendingRedemptions.set(key, request);
  request.then(
    () => pendingRedemptions.delete(key),
    () => pendingRedemptions.delete(key)
  );
  return request;
};

export const persistInvitationToken = (result, persistentStorage = localStorage, temporaryStorage = sessionStorage) => {
  const validated = validateInvitationResponse(result);
  // Writing first preserves an existing paid session if storage is unavailable.
  persistentStorage.setItem('token', validated.token);
  try { temporaryStorage.removeItem('token'); } catch {}
  return validated;
};
